import { ExclamationCircleOutlined, QuestionCircleOutlined } from "@ant-design/icons"
import { Button, Card, Flex, Form, Modal, ModalProps, Radio, Space, Switch, Tabs, Tooltip, Typography } from "antd"
import { startTransition, useCallback, useContext, useEffect, useState } from "react";
import { Blocker, useBlocker, useLoaderData, useNavigate } from "react-router-dom"
import { NotificationContext } from "../utils/notificationContext";
import { isObjectWithArrayEmpty } from "../utils/commonFunctions";
import { CheckboxGroupProps } from "antd/es/checkbox";
import { RadioChangeEvent } from "antd/lib";
import { SettingsService } from "../../services/settings.service";
import './settings.scss'

export interface ISettings {
    // reconcileFlag?: boolean,
    // phoneNumber?: boolean,
    // amount?: boolean,
    // comment?: boolean,
    rebateAdjustmentDate?: string,
    syncEnabled: boolean,
}

enum Setting {
    // reconcileFlag = "reconcileFlag",
    // phoneNumber = "phoneNumber",
    // amount = "amount",
    // comment = "comment",
    rebateAdjustmentDate = "rebateAdjustmentDate",
    syncEnabled = "syncEnabled"
}

export const Settings = () => {
    const loaderData = useLoaderData() as ISettings;
    const initialValues: ISettings = {
        // reconcileFlag: true,
        // phoneNumber: true,
        // amount: true,
        // comment: true,
        syncEnabled: loaderData?.syncEnabled ?? false,
        rebateAdjustmentDate: loaderData?.rebateAdjustmentDate ?? 'CommittedDate'
    }
    const [isDirty, setIsDirty] = useState(false);
    let blocker: Blocker | undefined = useBlocker(useCallback(() => isDirty, [isDirty]));
    const navigate = useNavigate()
    const [form] = Form.useForm();
    const [modalProps, setModalProps] = useState<ModalProps>();
    const [open, setOpen] = useState(false);
    const notificationContext = useContext(NotificationContext);
    const [formValues, setFormValues] = useState<ISettings>(initialValues);
    const settingsService = new SettingsService();

    useEffect(() => {
        if (blocker?.state === 'blocked') {
            onCancel(true);
        }
    }, [blocker])

    const OnValuesChange = (changedValues: any, allValues: any) => {
        if (changedValues) {
            setIsDirty(form.isFieldsTouched());
        }
    }

    const onCancel = async (isPrevent = false) => {
        setIsDirty(false);
        // if (!isPrevent) {
        //     blocker?.reset?.();
        // }
        const fieldsVal = form.getFieldsValue();
        if (form.isFieldsTouched() && !isObjectWithArrayEmpty(fieldsVal)) {
            setModalProps({
                title: <Flex><ExclamationCircleOutlined style={{ color: '#faad14', marginInlineEnd: '12px' }} />There are unsaved changes</Flex>,
                children: <Typography.Text style={{ marginLeft: '30px', display: 'flex', rowGap: '8px' }}>Your changes will not be saved</Typography.Text>,
                okText: 'Leave',
                cancelText: 'Cancel',
                centered: true,
                onOk() {
                    if (blocker) {
                        if (blocker?.state === 'unblocked') {
                            startTransition(() => navigate('/reconciliations'));
                        } else {
                            setTimeout(() => blocker?.proceed?.(), 0);
                        }
                    }
                },
                onCancel() {
                    setIsDirty(form.isFieldsTouched())
                    setOpen(false)
                },
                closable: false,
            })
            setOpen(true)
        } else {
            startTransition(() => navigate('/reconciliations'));
        }
    };

    const onSubmit = async () => {
        if(!isDirty) return;
        const data: ISettings = formValues;
        // if (formValues?.id) {
        //     template.version = formValues.version;
        //     template.id = formValues.id;
        // }
        const val = await settingsService.submitSettings(data);
        if (val) {
            setIsDirty(false);
            notificationContext.setNotification({
                type: 'success',
                message: 'Success!',
                description: `The settings submitted successfully.`
            })
            setTimeout(() => startTransition(() => navigate('/reconciliations')), 10)
        }
    };

    const radioList: CheckboxGroupProps['options'] = [
        { label: <>Adjust based on sale date <Tooltip title={"Vendor rebate adjustments will take effect on the date of the original sale transaction."}><QuestionCircleOutlined color="background: #00000073;" /></Tooltip></>, value: 'SaleDate', },
        { label: <>Adjust based on committed date <Tooltip title={"Vendor rebate adjustments will take effect on the date when the reconciliation file is committed in the system."}><QuestionCircleOutlined color="background: #00000073;" /></Tooltip></>, value: 'CommittedDate', },
    ]

    const onFormValueChange = (key: string, value: boolean | string) => {
        setFormValues({ ...formValues, [key]: value })
    }

    const items = [{
        key: '1',
        label: 'Data integration',
        destroyInactiveTabPane: true,
        children: <div style={{ width: '100%', paddingLeft: 16 }}>
            <Card size="default" style={{ width: 534 }} className="settings-card">
                <Form
                    form={form}
                    autoComplete="off"
                    name="control-hooks"
                    layout="inline"
                    initialValues={initialValues}
                    onFinish={onSubmit}
                    onValuesChange={OnValuesChange}>
                    <Flex wrap="wrap" vertical gap="large">
                        <Space>This setting enables configuration of data integration with your source POS system.</Space>
                        <Flex vertical gap={"small"}>
                            {/* <Flex gap={10} align="top">
                                <Form.Item name={Setting.reconcileFlag} label="Reconcile flag" colon={false} labelCol={{ push: 2 }}>
                                    <Switch onChange={(value) => onFormValueChange(Setting.reconcileFlag, value)} />
                                </Form.Item>
                            </Flex>
                            <Flex gap={10}>
                                <Form.Item name={Setting.phoneNumber} label="Phone number" colon={false} labelCol={{ push: 2 }}>
                                    <Switch onChange={(value) => onFormValueChange(Setting.phoneNumber, value)} />
                                </Form.Item>
                            </Flex>
                            <Flex gap={10}>
                                <Form.Item name={Setting.amount} label={
                                    <Flex gap={2}>
                                        Amount
                                        <Tooltip title={"The amount can only change once in RQ. "}><QuestionCircleOutlined color="background: #00000073;" /></Tooltip>
                                    </Flex>
                                } colon={false} labelCol={{ push: 2 }}>
                                    <Switch onChange={(value) => onFormValueChange(Setting.amount, value)} />
                                </Form.Item>
                            </Flex>
                            <Flex gap={10}>
                                <Form.Item name={Setting.comment} label="Comment" colon={false} labelCol={{ push: 2 }}>
                                    <Switch onChange={(value) => onFormValueChange(Setting.comment, value)} />
                                </Form.Item>
                            </Flex> */}
                            <Flex gap={10}>
                                <Form.Item name={Setting.syncEnabled} label="Synch data with RQ" colon={false} labelCol={{ push: 2 }}>
                                    <Switch onChange={(value) => onFormValueChange(Setting.syncEnabled, value)} />
                                </Form.Item>
                            </Flex>
                        </Flex>
                        <Flex gap={"large"} wrap>
                            <Space direction="vertical">
                                <Typography.Text strong>Rebate Adjustment Date</Typography.Text>
                                <Typography.Text>This date will determine when the vendor rebate adjustments will take effect. Any taxes associated with these vendor rebates will also be adjusted and will take effect on the same chosen date.</Typography.Text>
                            </Space>
                            <Form.Item name={Setting.rebateAdjustmentDate} colon={false} labelCol={{ push: 2 }}>
                                <Radio.Group options={radioList} onChange={(value: RadioChangeEvent) => onFormValueChange(Setting.rebateAdjustmentDate, value.target.value)} />
                            </Form.Item>
                        </Flex>
                        <Flex vertical align="flex-end" justify="space-between" gap={"large"}>
                            <Flex gap="small" wrap="wrap">
                                <Button type="default" onClick={() => onCancel()}>Cancel</Button>
                                <Button type="primary" htmlType="submit" disabled={!isDirty}>Save</Button>
                            </Flex>
                            <Modal {...modalProps} open={open}></Modal>
                        </Flex>
                    </Flex>
                </Form>
            </Card>
        </div>
    }]

    return <Tabs defaultActiveKey="1" items={items} type="card" size={"small"}
        style={{ backgroundColor: '#FFFFFF' }} className="settings-tabs"
    />
}